import { Injectable, ElementRef, signal } from '@angular/core';
import { BubbleConfig, BubbleLayout } from '../types/unlock-casino.types';

@Injectable()
export class BubbleLayoutService {
  layout = signal<BubbleLayout>({
    totalWidth: 0,
    totalHeight: 0,
    contentX: 0,
    contentY: 0,
    path: '',
    contentWidth: 0,
    contentHeight: 0,
  });

  private resizeObserver?: ResizeObserver;

  setupBubble(container: ElementRef<HTMLDivElement>, config: BubbleConfig): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = Math.round(entry.contentRect.width);
      const height = Math.round(entry.contentRect.height);
      const current = this.layout();

      if (width !== current.totalWidth || height !== current.totalHeight) {
        this.updateLayout(width, height, config);
      }
    });

    if (container?.nativeElement) {
      this.resizeObserver.observe(container.nativeElement);
    }
  }

  destroy(): void {
    this.resizeObserver?.disconnect();
  }

  private updateLayout(containerWidth: number, containerHeight: number, config: BubbleConfig): void {
    const result = this.generateBubblePath(containerWidth, containerHeight, config);
    this.layout.set(result);
  }

  private generateBubblePath(totalWidth: number, totalHeight: number, config: BubbleConfig): BubbleLayout {
    if (totalWidth <= 0 || totalHeight <= 0) {
      return { totalWidth: 0, totalHeight: 0, contentX: 0, contentY: 0, contentWidth: 0, contentHeight: 0, path: '' };
    }

    const stroke = config.borderWidth;

    const bodyWidth = totalWidth - config.tailSize - stroke;
    const bodyHeight = totalHeight - stroke;

    const startX = config.tailSize + stroke / 2;
    const startY = stroke / 2;

    const path = this.buildSVGPath(startX, startY, bodyWidth, bodyHeight, config);

    const contentX = startX + config.padding;
    const contentY = startY + config.padding;
    const contentWidth = Math.max(0, bodyWidth - config.padding * 2);
    const contentHeight = Math.max(0, bodyHeight - config.padding * 2);

    return {
      totalWidth,
      totalHeight,
      contentX,
      contentY,
      contentWidth,
      contentHeight,
      path,
    };
  }

  private buildSVGPath(
    startX: number,
    startY: number,
    bodyWidth: number,
    bodyHeight: number,
    config: BubbleConfig
  ): string {
    const stroke = config.borderWidth;

    const tailBase = config.tailBase;
    const tailHalf = tailBase / 2;

    const r = Math.min(config.borderRadius, bodyWidth / 2, Math.max(0, (bodyHeight - tailBase) / 2));

    const vCenter = startY + bodyHeight / 2;

    const tailTipX = stroke / 2;
    const ctrlX = startX - config.tailSize * 0.35;
    const ctrlY = tailHalf * 0.6;

    const pathCommands = [
      `M ${startX} ${startY + r}`,
      `Q ${startX} ${startY} ${startX + r} ${startY}`,
      `L ${startX + bodyWidth - r} ${startY}`,
      `Q ${startX + bodyWidth} ${startY} ${startX + bodyWidth} ${startY + r}`,
      `L ${startX + bodyWidth} ${startY + bodyHeight - r}`,
      `Q ${startX + bodyWidth} ${startY + bodyHeight} ${startX + bodyWidth - r} ${startY + bodyHeight}`,
      `L ${startX + r} ${startY + bodyHeight}`,
      `Q ${startX} ${startY + bodyHeight} ${startX} ${startY + bodyHeight - r}`,

      `L ${startX} ${vCenter + tailHalf}`,
      `Q ${ctrlX} ${vCenter + ctrlY} ${tailTipX} ${vCenter}`,
      `Q ${ctrlX} ${vCenter - ctrlY} ${startX} ${vCenter - tailHalf}`,

      'Z',
    ];

    return pathCommands.join(' ');
  }
}
